/* eslint-disable @typescript-eslint/ban-types */
import QueryTags from '../../types/QueryTags'
import { BaseResponse } from '../loans/types/response'
import {
  ViewBalanceRequest,
  ViewBalanceResponse,
  ViewAllbalancesResponse,
  ExportStatementResponse,
  ExportStatementRequest,
  MiniStatementResponse,
  AccountLimitResponse,
  AccountStatementResponse,
  AccountStatementRequest,
  AccountInsightsRequest,
  AccountInsightsResponse,
  EditAccountLimitRequest,
  EditAccountLimitsResponse,
  GetSavingsProductResponse,
  GetSavingsApplicationResponse,
  GetSavingsAccountTypesInfoResponse,
  OpenSimpleSavingsRequest,
  OpenSimpleSavingsResponse,
  OpenTermDepositResponse,
  OpenTermDepositRequest,
  RedeemTermDepositResponse,
  RedeemTermDepositRequest,
  GetCustomerSavingsAccountsResponse,
  SetTargetResponse,
  SetTargetRequest,
  UpdateTargetRequest,
  OpenSavingsAccountRequest,
  OpenSavingsAccountResponse,
  AccountOverdraftDetailsResponse,
} from './types'

import { EndpointBuilder } from '@reduxjs/toolkit/dist/query/endpointDefinitions'
import { BaseQueryFn, FetchArgs, FetchBaseQueryError, FetchBaseQueryMeta } from '@reduxjs/toolkit/query/react'
import GetTargetDetailsResponse from './types/response/getTargetDetails'

const basePath = '/inquiry-services/api/accounts'
const savingsPath = '/savings-service/api'

const accountEndpoints = (builder: EndpointBuilder<
  BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError, {}, FetchBaseQueryMeta>, QueryTags, 'data'>) =>
  ({
    getAccountBalance: builder.mutation<ViewBalanceResponse, ViewBalanceRequest>({
      query: (account) => {
        return `${basePath}/${account.accountNumber}/balance`
      }
    }),
    getAllAccountBalances: builder.query<ViewAllbalancesResponse, string>({
      query: (customer) => {
        return `${basePath}/${customer}/account-balances`
      }
    }),
    exportStatement: builder.query<ExportStatementResponse, ExportStatementRequest>({
      query: (body) => {
        const { startDate, endDate, accountNumber, email, fileType } = body
        return `${basePath}/${accountNumber}/send-statement?dateFrom=${startDate}&dateTo=${endDate}&fileType=${fileType}&email=${email}`
      }
    }),
    getMiniStatement: builder.query<MiniStatementResponse, any>({
      query: (accountNumber) => {
        return `${basePath}/${accountNumber}/mini-statement`
      },
    }),
    getAccountLimits: builder.query<AccountLimitResponse, any>({
      query: ({ profileId, accountNumber }) => {
        return `administration/api/customers/${profileId}/account/${accountNumber}/limits`
      }
    }),
    getAccountStatement: builder.query<AccountStatementResponse, AccountStatementRequest>({
      query: (body) => {
        const { accountId, dateFrom, dateTo, pageStart, pageSize } = body
        return `${basePath}/full-statement?accountId=${accountId}&dateFrom=${dateFrom}&dateTo=${dateTo}&pageStart=${pageStart}&pageSize=${pageSize}`
      }
    }),
    getAccountInsights: builder.query<AccountInsightsResponse, AccountInsightsRequest>({
      query: ({ accountNumber, year, month }) => `${basePath}/${accountNumber}/insights?year=${year}&month=${month}`
    }),
    editAccountLimits: builder.mutation<EditAccountLimitsResponse, EditAccountLimitRequest>({
      query: ({ profileId, accountNumber, transferAccountMaximumDailyLimit, transferAccountLimitPerTransaction }) => ({
        url: `administration/api/customers/${profileId}/account/${accountNumber}/limits`,
        method: 'PATCH',
        body: {
          transferLimits: {
            transferAccountMaximumDailyLimit,
            transferAccountLimitPerTransaction
          }
        }
      })
    }),
    getCustomerSavingsAccounts: builder.query<GetCustomerSavingsAccountsResponse[], { customerNumber: string, }>({
      query: ({ customerNumber }) => ({
        url: `${savingsPath}/savings/customers/${customerNumber}/accounts`,
        method: 'GET',
      }),
      providesTags: ['savingsAccounts'],
    }),
    getSavingsProductsVariations: builder.query<GetSavingsProductResponse[], void>({
      query: () => `${savingsPath}/product-variations`
    }),
    getSavingsApplications: builder.query<GetSavingsApplicationResponse[], { customerNumber: string, status: string,}>({
      query: (params) => ({
        url: `${savingsPath}/savings/applications`,
        method: 'GET',
        params,
      }),
      providesTags: ['savingsApplications'],
    }),
    getSavingsAccountTypesInfo: builder.mutation<GetSavingsAccountTypesInfoResponse[], { nationalId?: string, dob?: string, }>({
      query: (body) => ({
        url: `${savingsPath}/savings/accounts-types-info`,
        method: 'POST',
        body,
      })
    }),
    openSimpleSavings: builder.mutation<OpenSimpleSavingsResponse, OpenSimpleSavingsRequest>({
      query: (body) => ({
        url: `${savingsPath}/savings/open-simple-savings`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['savingsApplications'],
    }),
    openTermDeposit: builder.mutation<OpenTermDepositResponse, OpenTermDepositRequest>({
      query: (body) => ({
        url: `${savingsPath}/savings/open-term-deposit`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['savingsApplications'],
    }),
    openSavingsAccount: builder.mutation<OpenSavingsAccountResponse, OpenSavingsAccountRequest>({
      query: (body) => ({
        url: `${savingsPath}/savings/accounts`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['savingsApplications'],
    }),
    redeemTermDeposit: builder.mutation<RedeemTermDepositResponse, RedeemTermDepositRequest>({
      query: (body) => ({
        url: `${savingsPath}/savings/redeem-deposit`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['savingsAccounts'],
    }),
    setTarget: builder.mutation<SetTargetResponse, SetTargetRequest>({
      query: ({ accountNumber, ...body }) => ({
        url: `${savingsPath}/savings/set-target-saving/account/${accountNumber}`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: ['getTargetDetails'],
    }),
    updateTarget: builder.mutation<BaseResponse, UpdateTargetRequest>({
      query: ({ accountNumber, ...body }) => ({
        url: `${savingsPath}/savings/reschedule/target/account/${accountNumber}`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: ['getTargetDetails'],
    }),
    stopTarget: builder.mutation<BaseResponse, { scheduledTransferId: string, }>({
      query: ({ scheduledTransferId }) => ({
        url: `${savingsPath}/savings/stop/target/saving/${scheduledTransferId}`,
        method: 'PATCH',
      }),
      invalidatesTags: ['getTargetDetails'],
    }),
    getTargetDetails: builder.query<GetTargetDetailsResponse, { accountNumber: string, }>({
      query: ({ accountNumber }) => ({
        url: `${savingsPath}/savings/scheduled/target/${accountNumber}/details`,
        method: 'GET',
      }),
      providesTags: ['getTargetDetails'],
    }),
    getOverdraftDetails: builder.query<AccountOverdraftDetailsResponse, { accountNumber: string, }>({
      query: ({ accountNumber }) => ({
        url: `${basePath}/${accountNumber}/overdraft-details`,
        method: 'GET',
      }),
    }),
  })

export default accountEndpoints